import { groupBy, keyBy } from "lodash";

import { debug } from "@/utils/debug/logger";

import { apiBase, fetchUsingApi, fetchWithAuth } from "../core/apiClient";
import { ApiResponse } from "../core/types";
import { HouseholdData, HouseholdOwnership, HouseholdWithOwnership } from "../models/household";
import { enrichDocument, enrichHousehold, enrichProduct } from "../utils";

const convertHouseholdOwnership = (data: HouseholdOwnership): HouseholdWithOwnership[] => {
  const accountsById = keyBy(data.accounts, (account) => account.id);
  const accountsByHousehold = groupBy(data.relations, (relation) => relation.householdId);
  debug.log("convertHouseholdOwnership", data);

  return data.households.map((household) => {
    return {
      ...household,
      accounts: (accountsByHousehold[household.id] ?? []).map((relation) => accountsById[relation.accountId]),
    };
  });
};

export const fetchAllHouseholds = async (): Promise<ApiResponse<HouseholdWithOwnership[]>> => {
  return await fetchUsingApi(`${apiBase}/household/v1/list`, "GET", convertHouseholdOwnership);
};

export const fetchHouseholdData = async (
  householdId: number,
  language: string,
): Promise<ApiResponse<HouseholdData>> => {
  return await fetchUsingApi(
    `${apiBase}/household/v1/overview/${householdId}?language=${language}`,
    "GET",
    convertHouseholdData,
  );
};

const convertHouseholdData = (data: HouseholdData): HouseholdData => {
  const products = data.products.map(enrichProduct);
  const documents = data.documents.map(enrichDocument);
  const household = enrichHousehold(data.household);
  return {
    ...data,
    household,
    products,
    documents,
  };
};

export const updateWarrantyEndDate = async (
  householdId: number,
  warrantyEndDate: string,
): Promise<ApiResponse<boolean>> => {
  return await fetchUsingApi(
    `${apiBase}/household/v1/${householdId}/warranty-end-date`,
    "PUT",
    () => true,
    JSON.stringify({ warrantyEndDate }),
  );
};

export const updateHouseholdFeatureFlags = async (
  householdId: string,
  flags: Record<string, any | null>,
): Promise<void> => {
  await fetchWithAuth(`${apiBase}/household/v1/${householdId}/feature-flags`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(flags),
  });
};
